import moment from 'moment';
import type { UserProfileSettings } from 'venn-api';
import { getTitle } from '../pages/routes';
import { v1 as uuidv1 } from 'uuid';
import { isNil, pickBy } from 'lodash';
import { type AnyObject, assertExhaustive } from '../type';
import type { RangeType } from 'venn-ui-kit';
import { groupAnalyticsUser, identifyAnalyticsUser, trackAnalyticsPageView } from './fanout';

interface Traits {
  email?: string;
  firstName?: string;
  lastName?: string;
  organizationId?: number;
  organizationID?: number;
  organizationName?: string;
  workspaceId?: number;
  workspaceName?: string;
  company?: unknown;
  signupType?: string;
  actualUser?: boolean;
  isAccountAdmin: boolean;
  accountTier?: string;
  trialStatus?: string;
  createdAt?: string;
  investmentsAnalyzed?: number;
  investmentThreshold?: number;
}

const statusMap = {
  PENDING_ACTIVATION: 'whitelisted',
  BLACKLISTED: 'blacklisted',
  GREYLISTED_PENDING_ACTIVATION: 'greylisted',
};

const identify = (profile: UserProfileSettings) => {
  if (!profile?.user) {
    return;
  }

  const { organization, user } = profile;
  const traits: Traits = {
    email: user.email,
    firstName: profile?.firstName,
    lastName: profile?.lastName,
    actualUser: !!profile?.actualUser,
    isAccountAdmin: organization?.accountAdministrator === user.email,
    accountTier: organization?.accountTier,
    trialStatus: organization?.trial?.status,
    createdAt: moment(user.created).toISOString(),
  };

  if (organization) {
    // organization.id in reality world means workspace id
    // organization.accountId in reality world means organization id
    traits.organizationId = organization.accountId;
    traits.organizationID = organization.accountId;
    traits.organizationName = organization.accountName;
    traits.company = {
      id: organization.accountId,
      name: organization.accountName,
    };
    traits.workspaceId = organization.id;
    traits.workspaceName = organization.name;
  }

  identifyAnalyticsUser({
    userId: String(user.id),
    traits: { ...removeNilValues(traits) },
    intercomUserHash: profile.intercomHash,
  });
};

const dateFormat = (date?: number) => {
  if (date) {
    return moment.utc(date).format('YYYYMMDD');
  }
  return '';
};

const rangeFormat = (
  range?: RangeType,
): 'YTD' | '1Y' | '3Y' | '5Y' | '7Y' | '10Y' | 'FULL_HISTORY' | 'FULL_HISTORY_NO_FACTOR_CONSTRAINT' | undefined => {
  if (isNil(range)) {
    return undefined;
  }
  switch (range) {
    case 'ytd':
      return 'YTD';
    case '1yr':
      return '1Y';
    case '3yr':
      return '3Y';
    case '5yr':
      return '5Y';
    case '7yr':
      return '7Y';
    case '10yr':
      return '10Y';
    case 'full':
      return 'FULL_HISTORY';
    case 'full_no_factor_constraint':
      return 'FULL_HISTORY_NO_FACTOR_CONSTRAINT';
    default:
      return assertExhaustive(range);
  }
};

const group = (profile: UserProfileSettings) => {
  const org = profile?.organization;
  if (!org?.accountId) {
    return;
  }

  // accountId means a super group account id
  // several users could belong to one super account
  // several organizations/workspaces could belong to one super account
  const groupProperties = {
    groupType: 'Organization',
    name: org?.accountName,
    isPublicEquityIssuer: !!org?.issuesPublicEquity,
    accountAdmin: org?.accountAdministrator,
    accountTier: org?.accountTier,
    aumRange: org?.aum?.name,
    country: org?.country,
    jurisdication: org?.jurisdiction,
    locality: org?.locality,
    language: org?.language,
    createdAt: moment(org?.created).toISOString(),
    organizationType: org?.type,
    currency: org?.currency,
  };

  groupAnalyticsUser({ groupId: String(org.accountId), traits: removeNilValues(groupProperties) });
};

const removeNilValues = <T extends AnyObject>(obj: T): T => pickBy(obj, (value) => !isNil(value)) as T;

const AnalyticsUtils = (() => {
  let previousPageTitle: string;
  return {
    dateFormat,
    rangeFormat,
    identifyAndGroup: (profile: UserProfileSettings) => {
      identify(profile);
      group(profile);
    },

    identifyBeforeAccountCreation: (traits: Traits, status?: string) => {
      // If there's no userId, we create a random, anonymous identifier,
      // which will be overwritten once the user creates their account
      identifyAnalyticsUser({
        userId: uuidv1(),
        traits: {
          ...traits,
          // attempt to find the human-readable version of the status or show BE-generated status
          // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
          accountStatus: (status && statusMap[status]) || status,
        },
        intercomUserHash: undefined,
      });
    },

    updateAccountStatus: (traits: Traits, status: string | undefined, userId: number) => {
      identifyAnalyticsUser({
        userId: String(userId),
        traits: {
          ...traits,
          // attempt to find the human-readable version of the status or show BE-generated status
          // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
          accountStatus: status ? statusMap[status] : undefined,
        },
        intercomUserHash: undefined,
      });
    },

    page: (pathName: string) => {
      const title = getTitle(pathName);
      if (title && previousPageTitle !== title) {
        previousPageTitle = title;
        trackAnalyticsPageView({ title });
      }
    },
  };
})();

export default AnalyticsUtils;
